import { computed, inject, Injectable, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { BoardMigrationStatus } from '../../../model/board-migration-status';
import { BoardReductionGoal } from '../../../model/board-reduction-goal';
import { ClientBoard } from '../../../model/client-model/client-board';
import { Language } from '../../../model/enums/language';
import { boardFacadeActions, RecursivePropOfBoard, RecursiveValueOfBoard } from '../../../store/board/board.actions';
import {
  selectBoard,
  selectBoardLastSaved,
  selectBoardProperty,
  selectBoardSavingError,
  selectBoardViewModel,
  selectIsBoardLoading,
  selectIsBoardLoadingError,
  selectIsBoardSaving,
} from '../../../store/board/board.selectors';
import { BoardStore } from '../../../store/board/board.store';
import { BoardView, BoardViewModel, SelectBoardViewModel } from '../../../store/board/board.types';
import {
  selectActiveBoardMigrationInProgress,
  selectActiveBoardMigrationStatus,
} from '../../../store/migration-status/migration-status.selectors';
import { selectIsBoardOrInventoriesLoadingError } from '../../../store/shared/combined.selectors';
export type BoardUpdate = {
  property: RecursivePropOfBoard;
  value: RecursiveValueOfBoard;
};

/**
 * Facade service to interact with the board store.
 */
@Injectable({ providedIn: 'root' })
export class BoardFacadeService {
  private readonly _boardStore = inject(BoardStore);
  private _store = inject(Store);

  private _board$ = this._store.select(selectBoard);
  private _boardSignal = toSignal(this._board$);

  /**
   * Observable of the active boards migration status
   */
  get activeBoardMigrationStatus$(): Observable<{
    boardId: string | undefined;
    migrationStatus: BoardMigrationStatus;
  }> {
    return this._store.select(selectActiveBoardMigrationStatus);
  }

  /**
   * Observable of the board.
   */
  get board$(): Observable<ClientBoard> {
    return this._board$;
  }

  /**
   * Signal of the board.
   */
  get boardSignal(): Signal<ClientBoard | undefined> {
    return this._boardSignal;
  }

  /**
   * Observable of the board language.
   */
  get boardLanguage$(): Observable<Language> {
    return this._store.select(selectBoardProperty(['boardSettings', 'internationalisation', 'defaultLanguage']));
  }

  /**
   * Observable of the date the board was last saved.
   */
  get boardLastSaved$(): Observable<Date> {
    return this._store.select(selectBoardLastSaved);
  }

  /**
   * Observable of the whether a board resource is being migrated or not.
   */
  get isBoardResourceMigrationInProgress$(): Observable<BoardMigrationStatus['inProgress']> {
    return this._store.select(selectActiveBoardMigrationInProgress);
  }

  /**
   * Observable of whether the board is currently being loaded.
   */
  get isBoardLoading$(): Observable<boolean> {
    return this._store.select(selectIsBoardLoading);
  }

  /**
   * Observable of whether the board has failed to load.
   */
  get isBoardLoadingError$(): Observable<boolean> {
    return this._store.select(selectIsBoardLoadingError);
  }

  /**
   * Observable of whether the board or the parameters har failed to load.
   */
  get isBoardOrInventoriesLoadingError$(): Observable<boolean> {
    return this._store.select(selectIsBoardOrInventoriesLoadingError);
  }

  /**
   * Observable of whether the board is currently being saved.
   */
  get isBoardSaving$(): Observable<boolean> {
    return this._store.select(selectIsBoardSaving);
  }

  /**
   * Observable of whether the board failed saving
   */
  get isBoardSavingError$(): Observable<boolean> {
    return this._store.select(selectBoardSavingError);
  }

  /**
   * Call this when saving the board is done outside of the store.
   * The store will not be aware of those changes, and to avoid keeping an
   * outdated version of the board in the store we need to clear the store.
   */
  clearBoardStore(): void {
    this._store.dispatch(boardFacadeActions.clearBoardStore());
  }

  createReductionGoal(boardReductionGoal: Omit<BoardReductionGoal, 'id'>): void {
    this._store.dispatch(boardFacadeActions.createReductionGoal({ boardReductionGoal }));
  }

  deleteBoard(boardId: string, tenantId: string): void {
    this._store.dispatch(boardFacadeActions.deleteBoard({ boardId, tenantId }));
  }

  deleteReductionGoal(boardReductionGoal: Pick<BoardReductionGoal, 'id'>): void {
    this._store.dispatch(boardFacadeActions.deleteReductionGoal({ boardReductionGoal }));
  }

  getBoardVersion(boardId: string) {
    this._boardStore.loadVersion({ boardId });

    return computed(() => this._boardStore.version());
  }

  /**
   * Returns an observable of the specificed property of the board.
   * @param propertyPath The path to the property to get.
   */
  getBoardProperty$(propertyPath: RecursivePropOfBoard) {
    return this._store.select(selectBoardProperty(propertyPath));
  }

  /**
   * Returns an observable of the view model for the given view.
   * @param view The view to get the view model for.
   */
  getBoardViewModel$<T extends BoardView>(view: T): Observable<BoardViewModel[T]> {
    return this._store.select(<SelectBoardViewModel<T>>selectBoardViewModel[view]);
  }

  /**
   * Save the board with the given updates.
   * @param updates The updates to make to the board.
   */
  saveBoard({ updates }: { updates: BoardUpdate[] }): void {
    this._store.dispatch(boardFacadeActions.saveBoard({ updates }));
  }

  updateETagAndChildNodes(eTag: string, childNodes: string[]) {
    this._store.dispatch(boardFacadeActions.updateETagAndChildNodes({ _etag: eTag, childNodes }));
  }

  updateReductionGoal(boardReductionGoal: BoardReductionGoal): void {
    this._store.dispatch(boardFacadeActions.updateReductionGoal({ boardReductionGoal }));
  }
}
